import { useState, useEffect, useMemo } from 'react';
import { alphabetizeBy } from '@ion/mrdash';

import { useQueryIntegrations } from '@ion/api';

import { usePipelinesTableContext } from 'app/pipelines/PipelinesTable/PipelinesTableContext';

import buildRevisionOptionsById from './buildRevisionOptionsById';
import buildIntegrationSelectDisabledMessage from './buildIntegrationSelectDisabledMessage';
import buildRevisionSelectDisabledMessage from './buildRevisionSelectDisabledMessage';

// Static select field props
const STATIC_SELECT_PROPS = {
  size: 'small',
  variant: 'outlined',
};
const STATIC_INTEGRATION_SELECT_PROPS = {
  ...STATIC_SELECT_PROPS,
  label: 'Integration',
  'data-test-id': 'integrationSelect',
};
const STATIC_REVISION_SELECT_PROPS = {
  ...STATIC_SELECT_PROPS,
  label: 'Revision',
  'data-test-id': 'revisionSelect',
};

// "All" options to be injected into select field options lists
const DEFAULT_INTEGRATION_OPTION = {
  value: '_ALL_',
  label: 'All Integrations',
};
const DEFAULT_REVISION_OPTION = {
  value: '_ALL_',
  label: 'All Revisions',
};

export default function useIntegrationSelectProps() {
  const { setIntegrationIds, setPlaybookIds } = usePipelinesTableContext();
  const { data: integrations, loading, error } = useQueryIntegrations({ variables: { limit: null } });
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(DEFAULT_INTEGRATION_OPTION.value);
  const [selectedPlaybookId, setSelectedPlaybookId] = useState(DEFAULT_REVISION_OPTION.value);
  const revisionsByIntegrationId = useMemo(() => buildRevisionOptionsById(integrations), [integrations]);

  // Subscribe to changes to the integration and revision selects and notify the context
  useEffect(() => {
    /**
     * Map the selected integration and revision to a list to be used by the query
     *
     * In the future, we can easily change these filter fields to multi selects
     */
    const integrationIds = selectedIntegrationId === DEFAULT_INTEGRATION_OPTION.value ? [] : [selectedIntegrationId];
    const playbookIds = selectedPlaybookId === DEFAULT_REVISION_OPTION.value ? [] : [selectedPlaybookId];

    setIntegrationIds(integrationIds);
    setPlaybookIds(playbookIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIntegrationId, selectedPlaybookId]);

  // Integration select props
  const onIntegrationNameSelect = ({ target }) => {
    const integrationId = target.value;

    // Reset the revision select value to the default value when the integration is changed
    setSelectedPlaybookId(DEFAULT_REVISION_OPTION.value);
    setSelectedIntegrationId(integrationId);
  };
  const integrationNameSelectProps = {
    ...STATIC_INTEGRATION_SELECT_PROPS,
    value: selectedIntegrationId,
    options: [DEFAULT_INTEGRATION_OPTION, ...buildIntegrationNameOptions(integrations)],
    onChange: onIntegrationNameSelect,
    disabled: loading || Boolean(error) || !integrations.length,
    disabledMessage: buildIntegrationSelectDisabledMessage({ loading, error, integrations }),
  };

  // Revision select props
  const onRevisionSelect = ({ target }) => {
    const playbookId = target.value;

    setSelectedPlaybookId(playbookId);
  };
  const revisionSelectProps = {
    ...STATIC_REVISION_SELECT_PROPS,
    key: `${selectedIntegrationId}-revision-select`,
    value: selectedPlaybookId,
    options: [DEFAULT_REVISION_OPTION, ...(revisionsByIntegrationId[selectedIntegrationId] || [])],
    onChange: onRevisionSelect,
    disabled: loading || selectedIntegrationId === DEFAULT_INTEGRATION_OPTION.value,
    disabledMessage: buildRevisionSelectDisabledMessage({
      loading,
      error,
      selectedIntegrationId,
      defaultIntegrationId: DEFAULT_INTEGRATION_OPTION.value,
    }),
  };

  return {
    integrationNameSelectProps,
    revisionSelectProps,
  };
}

function buildIntegrationNameOptions(integrations) {
  if (!integrations.length) {
    return [];
  }

  const integrationOptions = integrations.map(({ id, name }) => ({ value: id, label: name }));

  return alphabetizeBy(integrationOptions, 'label');
}
